import { Clock, Package, Truck, CheckCircle, XCircle } from "lucide-react";

interface StatusUpdate {
  status: string;
  updated_at: string;
  note?: string;
}

interface OrderTimelineProps {
  status: string;
  updates?: StatusUpdate[];
}

export const OrderTimeline = ({ status, updates = [] }: OrderTimelineProps) => {
  const statusOrder = [
    "pending",
    "confirmed",
    "shipped",
    "delivered",
    "completed",
    "cancelled",
  ];
  const currentStatusIndex = statusOrder.indexOf(status);

  // If cancelled, we'll handle specially
  const isCancelled = status === "cancelled";

  const getStatusIcon = (status: string, isCurrent: boolean) => {
    const baseClass = "w-5 h-5";
    const activeClass = isCurrent ? "text-primary" : "text-muted-foreground";

    switch (status) {
      case "pending":
        return <Clock className={`${baseClass} ${activeClass}`} />;
      case "confirmed":
        return <Package className={`${baseClass} ${activeClass}`} />;
      case "shipped":
        return <Truck className={`${baseClass} ${activeClass}`} />;
      case "delivered":
        return <Truck className={`${baseClass} ${activeClass}`} />;
      case "completed":
        return <CheckCircle className={`${baseClass} ${activeClass}`} />;
      case "cancelled":
        return <XCircle className={`${baseClass} text-destructive`} />;
      default:
        return null;
    }
  };

  const getStatusLabel = (status: string) => {
    switch (status) {
      case "pending":
        return "Menunggu Konfirmasi";
      case "confirmed":
        return "Terkonfirmasi";
      case "shipped":
        return "Sedang Dikirim";
      case "delivered":
        return "Telah Diterima";
      case "completed":
        return "Pesanan Selesai";
      case "cancelled":
        return "Pesanan Dibatalkan";
      default:
        return status;
    }
  };

  const getUpdateTime = (status: string) => {
    const update = updates.find((u) => u.status === status);
    return update
      ? new Date(update.updated_at).toLocaleDateString("id-ID", {
          day: "numeric",
          month: "long",
          hour: "2-digit",
          minute: "2-digit",
        })
      : null;
  };

  return (
    <div className="relative">
      {/* Cancelled state (special case) */}
      {isCancelled && (
        <div className="flex items-start pb-6">
          <div className="relative">
            <div className="flex items-center justify-center rounded-full border-2 border-destructive w-8 h-8">
              {getStatusIcon("cancelled", true)}
            </div>
          </div>
          <div className="ml-4">
            <p className="font-medium text-destructive">Pesanan Dibatalkan</p>
            <p className="text-sm text-muted-foreground">
              {getUpdateTime("cancelled") || "Waktu pembatalan tidak tersedia"}
            </p>
          </div>
        </div>
      )}

      {/* Normal status progression */}
      {!isCancelled &&
        statusOrder
          .filter((s) => s !== "cancelled") // hilangkan 'cancelled' dari timeline biasa
          .map((s, index) => {
            const isComplete = index < currentStatusIndex;
            const isCurrent = index === currentStatusIndex;
            const isFuture = index > currentStatusIndex;
            const showLine = index < statusOrder.length - 2; // kurangi 1 karena 1 status dihapus

            return (
              <div key={s} className="flex items-start pb-6">
                <div className="relative">
                  <div
                    className={`flex items-center justify-center rounded-full border-2 ${
                      isComplete
                        ? "bg-primary border-primary"
                        : isCurrent
                        ? "border-primary"
                        : "border-muted-foreground/30"
                    } w-8 h-8`}
                  >
                    {getStatusIcon(s, isCurrent || isComplete)}
                  </div>
                  {showLine && (
                    <div
                      className={`absolute top-8 left-3.5 w-0.5 h-12 ${
                        isComplete ? "bg-primary" : "bg-muted-foreground/20"
                      }`}
                    />
                  )}
                </div>
                <div className="ml-4">
                  <p
                    className={`font-medium ${
                      isCurrent
                        ? "text-primary"
                        : isComplete
                        ? "text-foreground"
                        : "text-muted-foreground"
                    }`}
                  >
                    {getStatusLabel(s)}
                  </p>
                  {!isFuture && (
                    <p className="text-sm text-muted-foreground">
                      {getUpdateTime(s) || "Waktu update tidak tersedia"}
                    </p>
                  )}
                  {isCurrent && updates.find((u) => u.status === s)?.note && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {updates.find((u) => u.status === s)?.note}
                    </p>
                  )}
                </div>
              </div>
            );
          })}
    </div>
  );
};
